import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = {
  patient_name: "",
  patient_mobile: "",
  age: "",
  gender: "0",
  emr_station: "",
  email: "",
  agent_id: "0",
  whatsapp_optin: true,
};

// Admin-only: register a patient for a membership card WITHOUT payment.
// Posts to /admin/memberships and returns the created membership (with card url).
export default function RegisterMembershipModal({ open, onOpenChange, agents = [], onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.patient_name.trim()) return toast.error("Patient name is required");
    if (!/^\d{10}$/.test(form.patient_mobile)) return toast.error("Enter a valid 10-digit mobile");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/memberships", {
        patient_name: form.patient_name.trim(),
        patient_mobile: form.patient_mobile,
        age: form.age ? parseInt(form.age, 10) : 0,
        gender: parseInt(form.gender, 10) || 0,
        emr_station: form.emr_station.trim(),
        email: form.email.trim(),
        agent_id: parseInt(form.agent_id, 10) || 0,
        whatsapp_optin: form.whatsapp_optin,
      });
      toast.success(`Membership ${data.membership_number} created`);
      setForm(EMPTY);
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create membership");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Register membership</DialogTitle>
          <DialogDescription>Issues a card immediately. No payment is collected.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Full name *</label>
            <Input value={form.patient_name} onChange={set("patient_name")} placeholder="Full name as per ID" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Mobile *</label>
            <Input
              value={form.patient_mobile}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) => setForm((f) => ({ ...f, patient_mobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
              placeholder="10-digit mobile"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Age</label>
            <Input
              value={form.age}
              inputMode="numeric"
              onChange={(e) => setForm((f) => ({ ...f, age: e.target.value.replace(/\D/g, "").slice(0, 3) }))}
              placeholder="e.g. 35"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Gender</label>
            <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
              <SelectTrigger className="h-10"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Not specified</SelectItem>
                <SelectItem value="1">Male</SelectItem>
                <SelectItem value="2">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">EMR Station</label>
            <Input value={form.emr_station} onChange={set("emr_station")} placeholder="e.g. Andheri" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Email</label>
            <Input value={form.email} onChange={set("email")} placeholder="optional" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Assign agent</label>
            <Select value={form.agent_id} onValueChange={(v) => setForm((f) => ({ ...f, agent_id: v }))}>
              <SelectTrigger className="h-10"><SelectValue placeholder="None (Admin)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">None (Admin)</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.agent_id} value={String(a.agent_id)}>
                    {a.agent_name} (@{a.agent_username})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={form.whatsapp_optin}
              onChange={(e) => setForm((f) => ({ ...f, whatsapp_optin: e.target.checked }))}
            />
            Send card on WhatsApp
          </label>
        </div>

        <Button
          onClick={submit}
          disabled={saving}
          className="w-full h-11 bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {saving ? "Creating…" : "Register membership"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
